'use client';
import { UserProfile } from '@/components/common/navigation/sidebar/components/user-profile';
import Button from '@/components/global/button';
import {
  ChevronLeft,
  History,
  Settings,
  SquareUserRound,
} from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import React, { useMemo } from 'react';
import ROUTES from '@/lib/constants/routeConfig';
import { cn } from '@/lib/utils';
import { useI18n } from '@/locales/client';
import { memo } from 'react';
import { useUser } from '@/lib/hooks';

interface MenuItem {
  name: string;
  route: string;
  label: string;
  icon: JSX.Element;
  children?: MenuItem[];
  enabled?: boolean;
}

const ProfileSidebar: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { isLawyer } = useUser();
  const t = useI18n();

  const isMarketplace = pathname.includes('/marketplace/');

  const groupedMenu = useMemo<{ title?: string; items: MenuItem[] }[]>(() => [
    {
      title: 'Menu',
      items: [
        {
          name: 'personal-info',
          route: isMarketplace ? ROUTES.MARKETPLACE.PROFILE : ROUTES.DASHBOARD.PROFILE,
          label: t('dashboard.profile.personalInformation'),
          icon: <SquareUserRound className='h-5 w-5' />,
        },
        {
          name: 'payment-history',
          route: isMarketplace ? ROUTES.MARKETPLACE.BILLING : ROUTES.DASHBOARD.BILLING,
          label: t('dashboard.profile.paymentHistory'),
          icon: <History className='h-5 w-5' />,
        },
      ],
    },
  ], [isMarketplace, t]);

  const MemoizedUserProfile = memo(UserProfile);

  const selectedTab = useMemo(() => {
    for (const section of groupedMenu) {
      for (const item of section.items) {
        if (pathname === item.route) return item.name;
        if (item.children) {
          for (const child of item.children) {
            if (pathname === child.route) return child.name;
          }
        }
      }
    }
    return null;
  }, [pathname, groupedMenu]);

  const goTo = (route: string) => router.push(route);

  return (
    <div className='border-gray-[#E5E7EB] flex h-[calc(100vh-72px)] w-full flex-col border-r bg-white dark:border-gray-700 dark:bg-gray-900'>
      <div className='flex h-full flex-col'>
        <div className='flex-1 overflow-y-auto p-6'>
          <div className='flex flex-col gap-6'>
            {isLawyer && (
              <Button
                variant='neutral'
                size='sm'
                className='w-full gap-2'
                onClick={() => goTo('/dashboard')}
              >
                <ChevronLeft className='h-4 w-4' /> {t('dashboard.profile.back')}
              </Button>
            )}

            {groupedMenu.map((section, idx) => (
              <div key={idx} className='flex flex-col gap-2'>
                {section.title && (
                  <span className='text-xs font-semibold uppercase text-muted-foreground'>
                    {section.title}
                  </span>
                )}

                {section.items.map(
                  ({
                    name,
                    label,
                    route,
                    icon,
                    children,
                    enabled = true,
                  }) => (
                    <div key={route} className='flex flex-col gap-1'>
                      <Button
                        variant='ghost'
                        size='lg'
                        disabled={!enabled}
                        onClick={() => !children && enabled && goTo(route)}
                        className={cn(
                          'justify-start gap-2',
                          selectedTab === name &&
                            !children &&
                            'bg-muted font-semibold text-foreground',
                          !enabled && 'cursor-not-allowed opacity-50'
                        )}
                      >
                        {icon}
                        {label}
                      </Button>

                      {children && (
                        <div className='ml-6 flex flex-col gap-1'>
                          {children.map(
                            ({
                              name: childName,
                              label: childLabel,
                              icon: childIcon,
                              route: childRoute,
                              enabled: childEnabled = true,
                            }) => (
                              <Button
                                key={childRoute}
                                variant='ghost'
                                size='sm'
                                disabled={!childEnabled}
                                onClick={() =>
                                  childEnabled && goTo(childRoute)
                                }
                                className={cn(
                                  'justify-start gap-2 pl-2',
                                  selectedTab === childName &&
                                    'bg-muted font-semibold text-foreground',
                                  !childEnabled &&
                                    'cursor-not-allowed opacity-50'
                                )}
                              >
                                {childIcon}
                                {childLabel}
                              </Button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        </div>

        <div className='shrink-0 border-t border-gray-200 p-6 dark:border-gray-700'>
          <MemoizedUserProfile />
        </div>
      </div>
    </div>
  );
};

export default memo(ProfileSidebar);
